import { useEffect, useRef } from 'react';
import { useFeature } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'types';

interface UseInlineEditClickHandlingProps {
  handleSubmit: (onSubmit: () => void) => () => void;
  onSubmit: () => void;
  disableClickOffInlineEdit?: boolean;
}

export const useInlineEditClickHandling = ({
  handleSubmit,
  onSubmit,
  disableClickOffInlineEdit = false,
}: UseInlineEditClickHandlingProps) => {
  const tempClickOffInlineEdit = useFeature(
    FeatureFlags.tempClickOffInlineEdit
  ).on;
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!tempClickOffInlineEdit || disableClickOffInlineEdit) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(event.target as Node)) {
        const target = event.target as Element;

        // Check if the click is on a popup/dialog that's tagged as related to this form
        const isFormRelatedPopup = target.closest('[data-inline-edit-dialog]');

        // Also check for common dropdown/select elements that might be related
        const isDropdownOption =
          target.closest('.MuiAutocomplete-popper') ||
          target.closest('.MuiSelect-menu') ||
          target.closest('.MuiPickersPopper-root') ||
          target.closest('.MuiPickersCalendar') ||
          target.closest('.MuiPickersDay-root') ||
          target.closest('.MuiPickersMonth-root') ||
          target.closest('.MuiPickersYear-root') ||
          target.closest('.MuiPickersCalendarHeader') ||
          target.closest('.MuiPickersToolbar') ||
          target.closest('.MuiPickersCalendarHeader-switchViewButton') ||
          target.closest('.MuiMenu-list');

        if (!isFormRelatedPopup && !isDropdownOption) {
          handleSubmit(onSubmit)();
        }
      }
    };

    const handleClickInside = (event: MouseEvent) => {
      // Check if the click is on the Undo/Exit Edit button or its children
      const target = event.target as Element;
      const isUndoButton =
        target.closest('button[aria-label*="Undo"]') ||
        target.closest('button[aria-label*="Exit"]') ||
        target.closest('[role="button"]');

      if (isUndoButton && formRef.current?.contains(target)) {
        // Don't submit when clicking the Undo/Exit button
        event.stopPropagation();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    formRef.current?.addEventListener('mousedown', handleClickInside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      const currentFormRef = formRef.current;
      if (currentFormRef) {
        currentFormRef.removeEventListener('mousedown', handleClickInside);
      }
    };
  }, [
    tempClickOffInlineEdit,
    handleSubmit,
    onSubmit,
    disableClickOffInlineEdit,
  ]);

  return {
    formRef,
    tempClickOffInlineEdit:
      tempClickOffInlineEdit && !disableClickOffInlineEdit,
  };
};
